import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    StyleSheet,
    View,
    TouchableOpacity,
    Text,
    TextInput,
    ScrollView
} from 'react-native';
import { createPortal } from 'react-dom';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons, AntDesign } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import Data from './Data';
import { RepeaterState, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Modal from "react-native-modal";
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// 스크롤 가능한 영역 안에서 발생한 이벤트인지 판별 (모달 내부 드롭다운 등)
const isInScrollable = (target: any) => {
    let n: any = target;
    while (n && n !== document.body && n.nodeType === 1) {
        const ov = getComputedStyle(n).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
        n = n.parentElement;
    }
    return false;
};

const blockDrag = (el: any) => {
    if (!el || el._bd) return;
    el._bd = true;
    const pd = (e: Event) => { if (e.type === 'wheel' && isInScrollable(e.target)) return; e.preventDefault(); };
    el.addEventListener('mousedown', pd);
    el.addEventListener('touchstart', pd, { passive: false });
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('dragstart', pd);
};
const blockContentDrag = (el: any) => {
    if (!el || el._bcd) return;
    el._bcd = true;
    const pd = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('touchmove', pd, { passive: false });
};

// ─── 테이블 컬럼 ──────────────────────────────────────
const repeaterColumns = [
    {
        id: 1,
        name: '담당 구역',
        selector: (row: any) => row.AREA,
    },
    {
        id: 2,
        name: '병실 호수',
        selector: (row: any) => row.ROOM_NO,
    },
    {
        id: 3,
        name: '중계기 ID',
        selector: (row: any) => row.ID,
    },
    {
        id: 4,
        name: "Ipv4 주소",
        selector: (row: any) => row.IPV4,
    },
    {
        id: 5,
        name: "상태정보",
        selector: (row: any) => RepeaterState(row.CONNECT),
    },
    {
        id: 6,
        name: "버전",
        selector: (row: any) => row.VERSION,
    },
    {
        id: 7,
        name: "REL버전",
        selector: (row: any) => row.RELEASE,
    },
];

// ─── 모달 ─────────────────────────────────────────────
const OpenModal = ({ modalVisible, setModalVisible, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [relVer, setRelVer] = useState('');
    const onChangeVer = (text: any) => { setRelVer(text) };

    useEffect(() => {
        if (modalVisible) setRelVer('');
    }, [modalVisible]);

    const onUpdate = () => {
        Data.fetchData('repeater/updatAllRepeaterRelver', {
            RELEASE: relVer
        }, (result: any, count: any) => {
            if (count > 0) {
                setRelVer('');
                alert('변경되었습니다.');
                setModalVisible(false);
            } else {
                alert('변경에 실패하였습니다.');
            }
        }, true);
    };

    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={modalVisible}
            backdropOpacity={0.7}
            onBackdropPress={() => { setModalVisible(false); }}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text, textAlign: 'center' }}>
                    모든 중계기 REL버전 업데이트
                </Text>
                <Text style={{ fontSize: s(13), color: T.textSub, textAlign: 'center', marginTop: s(6), marginBottom: s(20) }}>
                    업데이트할 REL 버전을 입력하세요
                </Text>
                <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <TextInput
                        autoCapitalize='none'
                        clearButtonMode='always'
                        style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                        onChangeText={(text: any) => { onChangeVer(text) }}
                        value={relVer}
                        placeholder="예: 1.0.0"
                        placeholderTextColor={T.muted}
                    />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(10), marginTop: s(24) }}>
                    <TouchableOpacity
                        style={[styles.modalBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: 'transparent' }]}
                        onPress={() => { setModalVisible(false); }}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '600', color: T.textSub }}>취소</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.modalBtn, { backgroundColor: S.warn }]}
                        onPress={() => { onUpdate() }}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>업데이트</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

// ─── 중계기 등록 모달 ─────────────────────────────────
const AddRepeaterModal = ({ visible, setVisible, areaList, onSuccess, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [form, setForm] = useState({ ROOM_NO: '', ID: '', IPV4: '', AREA: '' });
    const [areaOpen, setAreaOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (visible) {
            setForm({ ROOM_NO: '', ID: '', IPV4: '', AREA: '' });
            setAreaOpen(false);
        }
    }, [visible]);

    const set = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSubmit = () => {
        if (!form.AREA) { alert('구역을 선택해주세요.'); return; }
        if (!form.ROOM_NO.trim()) { alert('병실 호수를 입력해주세요.'); return; }
        if (!form.ID.trim()) { alert('중계기 ID를 입력해주세요.'); return; }
        if (!form.IPV4.trim()) { alert('IPv4 주소를 입력해주세요.'); return; }
        setSubmitting(true);
        Data.fetchData('tabRepeater/insertNewRepeater', {
            ROOM_NO: form.ROOM_NO,
            ID: form.ID,
            IPV4: form.IPV4,
            AREA: form.AREA,
        }, (result: any, count: any) => {
            setSubmitting(false);
            if (count === 1) {
                alert('중계기가 등록되었습니다.');
                setVisible(false);
                onSuccess();
            } else if (count === -2) {
                alert('이미 등록된 병실 호수입니다.');
            } else if (count === -3) {
                alert('이미 등록된 중계기 ID입니다.');
            } else {
                alert('등록에 실패하였습니다.');
            }
        }, true);
    };

    if (!visible) return null;
    return createPortal(
        <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
            <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                <TouchableOpacity activeOpacity={1} onPress={() => setVisible(false)} style={{ flex: 1 }} />
            </View>
            <View ref={blockContentDrag} style={[styles.registerModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                {/* 헤더 */}
                <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text, textAlign: 'center' }}>
                    중계기 등록
                </Text>
                <Text style={{ fontSize: s(13), color: T.textSub, textAlign: 'center', marginTop: s(6), marginBottom: s(20) }}>
                    새 중계기 정보를 입력하세요
                </Text>

                {/* 구역 선택 */}
                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>구역 *</Text>
                    <TouchableOpacity
                        style={[styles.dropdown, { backgroundColor: T.cardInner, borderColor: T.border }]}
                        onPress={() => setAreaOpen(!areaOpen)}
                    >
                        <Text style={{ fontSize: s(14), color: form.AREA ? T.text : T.muted }}>
                            {form.AREA || '구역을 선택하세요'}
                        </Text>
                        <Ionicons name={areaOpen ? 'chevron-up' : 'chevron-down'} size={s(16)} color={T.muted} />
                    </TouchableOpacity>
                    {areaOpen && (
                        <View style={[styles.dropdownList, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <ScrollView style={{ maxHeight: s(200) }} nestedScrollEnabled showsVerticalScrollIndicator>
                            {areaList.map((a: string, i: number) => (
                                <TouchableOpacity key={i}
                                    style={[styles.dropdownItem, {
                                        borderBottomColor: T.border,
                                        backgroundColor: form.AREA === a ? S.accent + '12' : 'transparent',
                                    }]}
                                    onPress={() => { set('AREA', a); setAreaOpen(false); }}
                                >
                                    <Text style={{ fontSize: s(13), fontWeight: form.AREA === a ? '700' : '500', color: form.AREA === a ? S.accent : T.text }}>
                                        {a}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </ScrollView>
                        </View>
                    )}
                </View>

                {/* 병실 호수 */}
                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>병실 호수 *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize='none' placeholder='예: 101' placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('ROOM_NO', v)} value={form.ROOM_NO} />
                    </View>
                </View>

                {/* 중계기 ID */}
                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>중계기 ID *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize='none' placeholder='중계기 ID 입력' placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('ID', v)} value={form.ID} />
                    </View>
                </View>

                {/* IPv4 */}
                <View style={{ marginBottom: s(6) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>IPv4 주소 *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize='none' placeholder='예: 192.168.0.1' placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('IPV4', v)} value={form.IPV4} />
                    </View>
                </View>

                {/* 버튼 */}
                <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(10), marginTop: s(24) }}>
                    <TouchableOpacity
                        style={[styles.modalBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: 'transparent' }]}
                        onPress={() => setVisible(false)}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '600', color: T.textSub }}>취소</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.modalBtn, { backgroundColor: S.accent, opacity: submitting ? 0.5 : 1 }]}
                        onPress={handleSubmit}
                        disabled={submitting}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>
                            {submitting ? '등록 중...' : '등록'}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>,
        document.body
    );
};

// ─── 메인 컴포넌트 ────────────────────────────────────
export default function Repeater({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [list, setList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((list) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    const pageData = ((list) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [modalVisible, setModalVisible] = useState(false);
    const [admin, setAdmin] = useState('1');
    const [registerModal, setRegisterModal] = useState(false);
    const [areaList, setAreaList] = useState<string[]>([]);
    const { dark, setDark } = useDarkMode();
    const repeaterTimerRef = useRef<any>(null);

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    useEffect(() => {
        if (!isFocused) return;
        getRepeatersList();
        Data.fetchData('group/getAreaList', {}, (result: any, count: any) => {
            if (count > 0) {
                setAreaList(result.map((a: any) => a.AREA));
            }
        }, true);
        return () => {
            if (repeaterTimerRef.current)
                clearTimeout(repeaterTimerRef.current);
        };
    }, [isFocused]);

    useEffect(() => {
        const anyModal = modalVisible || registerModal;
        if (!anyModal) return;
        const prevent = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', prevent, { passive: false } as any);
        document.addEventListener('touchmove', prevent, { passive: false } as any);
        return () => {
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
        };
    }, [modalVisible, registerModal]);

    const getRepeatersList = async (manual: boolean = false) => {
        if (repeaterTimerRef.current) {
            clearTimeout(repeaterTimerRef.current);
            repeaterTimerRef.current = null;
        }

        const userInfo = await getStorage('AutoLogin');
        const adminValue = userInfo?.admin ?? '1';
        setAdmin(adminValue);

        const endpoint = adminValue === '0' ? 'repeater/getRepeatersList' : 'repeater/getRepeatersListbyUser';

        Data.fetchData(endpoint, { AREA: "" }, (result: any) => {
            setList(result || []);
        }, true);

        if (!manual) {
            repeaterTimerRef.current = setTimeout(() => getRepeatersList(), 10000);
        }
    };

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>중계기 목록</Text>
                        {list?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        {admin === '0' && (
                            <TouchableOpacity style={[styles.relButton, { backgroundColor: S.accent }]} onPress={() => setRegisterModal(true)}>
                                <Ionicons name="add" size={s(14)} color="#fff" style={{ marginRight: s(5) }} />
                                <Text style={{ color: '#fff', fontSize: s(13), fontWeight: '700' }}>중계기 등록</Text>
                            </TouchableOpacity>
                        )}
                        {admin === '0' && (
                        <TouchableOpacity style={styles.relButton} onPress={() => setModalVisible(true)}>
                            <AntDesign name="upload" size={s(14)} color="#fff" style={{ marginRight: s(5) }} />
                            <Text style={{ color: '#fff', fontSize: s(13), fontWeight: '700' }}>REL버전 전체업데이트</Text>
                        </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={repeaterColumns}
                    defaultSortFieldId={1}
                    data={pageData}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(16), color: T.muted }}>
                                등록된 중계기가 없습니다.
                            </Text>
                        </View>
                    }
                    onRowClicked={(row: any) => {
                        try { window.localStorage.setItem(`_navParams_RepeaterDetail_${row.ID}`, JSON.stringify({ data: row })); } catch {}
                        navigation.navigate('RepeaterDetail', {
                            id: String(row.ID),
                            onDeleted: (deletedID: string) => {
                                setList((prevList: any) => prevList.filter((item: any) => item.ID !== deletedID));
                            }
                        })
                    }}
                />
                {(list || []).length > 0 && (
                    <TablePagination
                        rowCount={(list || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
            <OpenModal
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
                T={T}
            />
            <AddRepeaterModal
                visible={registerModal}
                setVisible={setRegisterModal}
                areaList={areaList}
                onSuccess={() => getRepeatersList(true)}
                T={T}
            />
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    relButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: S.warn,
        borderRadius: s(10),
        paddingHorizontal: s(14),
        height: s(34),
    },
    modalView: {
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(30),
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: s(4),
        elevation: 5,
        width: '35%',
    },
    modalInputBox: {
        borderWidth: 1,
        borderRadius: s(10),
        height: s(40),
    },
    modalBtn: {
        paddingHorizontal: s(22),
        height: s(38),
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: s(10),
    },
    registerModal: {
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(30),
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: s(12),
        elevation: 8,
        width: '35%',
        maxWidth: s(480),
    },
    dropdown: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(14),
        height: s(42),
    },
    dropdownList: {
        borderWidth: 1,
        borderRadius: s(10),
        marginTop: s(4),
        overflow: 'hidden',
    },
    dropdownItem: {
        paddingHorizontal: s(14),
        paddingVertical: s(10),
        borderBottomWidth: 1,
    },
});
